import React, { useEffect, useState } from "react";
import { Eye, Users, CalendarDays, LogIn, Smartphone, Monitor } from "lucide-react";
import { Page, Card, StatCard, EmptyState } from "../components/UI";
import api from "../lib/api";

const RANGES = [7, 30, 90];

const Bars = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.views));
  return (
    <div className="flex items-end gap-1 h-40" data-testid="stats-chart">
      {data.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center justify-end group relative min-w-0">
          <div className="w-full rounded-t-md transition-colors" style={{ height: `${(d.views / max) * 100}%`, minHeight: 3, background: "var(--mf-primary)", opacity: 0.85 }} />
          <div className="absolute -top-8 hidden group-hover:block text-[10px] px-2 py-1 rounded-md whitespace-nowrap z-10" style={{ background: "var(--mf-text)", color: "var(--mf-bg)" }}>{d.date.slice(5)} · {d.views} views · {d.visitors} visitors</div>
        </div>
      ))}
    </div>
  );
};

export default function AdminStats() {
  const [days, setDays] = useState(30);
  const [s, setS] = useState(null);
  useEffect(() => { setS(null); api.get("/admin/analytics", { params: { days } }).then(({ data }) => setS(data)); }, [days]);

  return (
    <Page title="Website Stats" subtitle="Page visits and sign-ins. Privacy-safe: no client data, IDs stripped from page addresses."
      actions={<div className="flex gap-1">{RANGES.map((r) => (
        <button key={r} onClick={() => setDays(r)} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: days === r ? "var(--mf-primary)" : "var(--mf-surface)", color: days === r ? "#FAFAF8" : "var(--mf-text)" }} data-testid={`stats-range-${r}`}>{r} days</button>
      ))}</div>}>
      {!s ? <div className="mf-skeleton h-60" /> : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Page views" value={s.total_views} icon={Eye} accent="var(--mf-primary)" delay={0.05} testid="stats-views" />
            <StatCard label="Unique visitors" value={s.unique_visitors} icon={Users} accent="var(--mf-accent)" delay={0.1} testid="stats-visitors" />
            <StatCard label="Views today" value={s.views_today} icon={CalendarDays} accent="#7BAE7F" delay={0.15} testid="stats-today" />
            <StatCard label="Sign-ins" value={s.logins.successful + s.logins.client_portal} icon={LogIn} accent="#B45253" delay={0.2} testid="stats-logins" />
          </div>
          <Card delay={0.25} testid="stats-daily">
            <h3 className="font-serif text-xl mb-4">Daily visits</h3>
            {s.by_day.length ? <Bars data={s.by_day} /> : <EmptyState icon={Eye} title="No visits yet" description="Visits will appear here as people open the site." />}
          </Card>
          <div className="grid md:grid-cols-2 gap-5">
            <Card delay={0.3} testid="stats-top-pages">
              <h3 className="font-serif text-xl mb-3">Top pages</h3>
              {s.top_pages.map((p) => (
                <div key={p.path} className="flex justify-between text-sm py-1.5 border-b last:border-0" style={{ borderColor: "var(--mf-muted)" }}><span className="truncate">{p.path}</span><span className="font-medium">{p.views}</span></div>
              ))}
              <div className="flex gap-4 mt-4 text-xs" style={{ color: "var(--mf-text-muted)" }}>
                <span className="flex items-center gap-1"><Monitor className="w-3.5 h-3.5" /> Desktop {s.devices.desktop || 0}</span>
                <span className="flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> Mobile {s.devices.mobile || 0}</span>
              </div>
              {s.referrers.length > 0 && <div className="text-xs mt-2" style={{ color: "var(--mf-text-muted)" }}>Top referrers: {s.referrers.map((r) => `${r.host} (${r.n})`).join(", ")}</div>}
            </Card>
            <Card delay={0.35} testid="stats-recent-logins">
              <h3 className="font-serif text-xl mb-1">Recent sign-ins</h3>
              <div className="text-xs mb-3" style={{ color: "var(--mf-text-muted)" }}>Clinician {s.logins.successful} · Client portal {s.logins.client_portal} · Denied {s.logins.denied}</div>
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {s.recent_logins.map((l, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 gap-2" data-testid="stats-login-row">
                    <span className="truncate">{l.email}</span>
                    <span className="text-xs whitespace-nowrap" style={{ color: l.action === "login_denied" ? "#b91c1c" : "var(--mf-text-muted)" }}>
                      {l.action === "login_denied" ? "denied" : l.role} · {new Date(l.timestamp).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
    </Page>
  );
}
